import { coreGet, coreMutate } from "./api-client";

export type EmailIntelligenceState =
  | "PENDING"
  | "RUNNING"
  | "REVIEW_REQUIRED"
  | "ACCEPTED"
  | "CORRECTED"
  | "REJECTED"
  | "FAILED";

export type EmailNature =
  | "NEW_INQUIRY"
  | "CUSTOMER_REPLY"
  | "STAFF_REPLY"
  | "AUTOMATED_NOTIFICATION"
  | "NON_INQUIRY_BUSINESS"
  | "MARKETING"
  | "SPAM_OR_PHISHING"
  | "UNKNOWN";

export interface EmailIntelligenceThread {
  conversationId: string;
  subject: string;
  messageCount: number;
  latestDirection: "INBOUND" | "OUTBOUND" | null;
  latestAt: string;
  counterpartDomain: string | null;
  state: EmailIntelligenceState;
  invocationId: string | null;
  organizationName: string | null;
  relationshipStage: string | null;
  purchaseIntent: string | null;
  riskLevel: string | null;
  confidence: number | null;
  primaryNature: EmailNature | null;
  inquiryCandidate: boolean | null;
  reviewDecision: string | null;
}

export interface SafeEmailMessage {
  messageRef: string;
  direction: "INBOUND" | "OUTBOUND";
  observedAt: string;
  subject: string;
  excerpt: string;
  bodyText?: string;
  senderEmail?: string | null;
  recipientEmails?: string[];
  bodyAvailable?: boolean;
  bodySource?: "TEXT" | "HTML_TO_SAFE_TEXT" | "MISSING";
  bodyBlocks?: Array<{
    blockRef: string;
    kind:
      | "CURRENT_CONTENT"
      | "QUOTED_REPLY"
      | "FORWARDED_MESSAGE"
      | "SIGNATURE";
    text: string;
    truncated?: boolean;
  }>;
  organizationDomain: string | null;
  mailboxCategory: "INBOX" | "SPAM";
  attachmentSummary: {
    count: number;
    contentTypes: string[];
    totalBytes: number;
  };
}

export interface EmailClassification {
  messageRef: string;
  nature: EmailNature;
  inquiryCandidate: boolean;
  customerProfileSignal: boolean;
  confidence: number;
  reasons: string[];
  evidenceSummary: string;
}

export interface CustomerProfileCandidate {
  organizationName: string | null;
  organizationType: string;
  probableCountryCode: string | null;
  relationshipStage: string;
  purchaseIntent: string;
  commercialPotential: string;
  engagementQuality: string;
  riskLevel: string;
  riskSignals: string[];
  missingInformation: string[];
  summary: string;
  confidence: number;
}

export interface EmailIntelligenceDetail {
  conversationId: string;
  subject: string;
  customerEmail?: string | null;
  originalInquiry?: {
    sourceInquiryId: string;
    submittedAt: string;
    email: string | null;
    contactName: string | null;
    companyName: string | null;
    productDisplayName: string | null;
    cas: string | null;
    quantity: string | null;
    package: string | null;
    purity: string | null;
    researchUse: string | null;
    comment: string | null;
    fromPage: string | null;
    pagePath: string | null;
  } | null;
  state: EmailIntelligenceState;
  invocationId: string | null;
  invocationStatus: string | null;
  messages: SafeEmailMessage[];
  result: {
    schemaVersion: "1.0";
    messageClassifications: EmailClassification[];
    customerProfileCandidate: CustomerProfileCandidate;
    humanReview: { required: true; reasons: string[] };
    injectionWarnings: string[];
  } | null;
  customerResearch?: {
    invocationId: string;
    status: string;
    createdAt: string;
    result: Record<string, unknown> | null;
  } | null;
  reviews: Array<{
    id: string;
    decision: string;
    reason: string | null;
    reviewedAt: string;
  }>;
  history: Array<{
    invocationId: string;
    status: string;
    createdAt: string;
    completedAt: string | null;
    reviewDecision: string | null;
  }>;
}

export interface EmailIntelligenceAutomation {
  enabled: boolean;
  businessAutomationEnabled: boolean;
  phase: "CANARY" | "FULL";
  workspace: string | null;
  canaryLimit: number;
  dailyMessageLimit: number;
  maxMessagesPerWindow: number;
  concurrency: number;
  spamLookbackDays: number;
  autoApplyKillSwitch: boolean;
  externalDraftKillSwitch: boolean;
}

export function loadEmailIntelligenceThreads(
  status?: EmailIntelligenceState,
  signal?: AbortSignal,
) {
  const query = new URLSearchParams({ take: "500" });
  if (status) query.set("status", status);
  return coreGet<{
    items: EmailIntelligenceThread[];
    automation: EmailIntelligenceAutomation;
  }>(`/email-intelligence/threads?${query.toString()}`, signal);
}

export function loadEmailIntelligenceDetail(
  conversationId: string,
  signal?: AbortSignal,
) {
  return coreGet<EmailIntelligenceDetail>(
    `/email-intelligence/threads/${encodeURIComponent(conversationId)}`,
    signal,
  );
}

export function reviewEmailIntelligence(
  conversationId: string,
  input: {
    expectedInvocationId: string;
    decision: "ACCEPT" | "ACCEPT_WITH_CHANGES" | "REJECT";
    changes?: Record<string, unknown>;
    reason: string;
  },
) {
  return coreMutate(
    `/email-intelligence/threads/${encodeURIComponent(conversationId)}/review`,
    "POST",
    { changes: {}, ...input },
  );
}

export function retryEmailIntelligence(conversationId: string) {
  return coreMutate(
    `/email-intelligence/threads/${encodeURIComponent(conversationId)}/retry`,
    "POST",
    {},
  );
}

export function researchEmailCustomer(conversationId: string) {
  return coreMutate<{ invocationId: string }>(
    `/email-intelligence/threads/${encodeURIComponent(conversationId)}/customer-research`,
    "POST",
    {},
  );
}

export function startEmailIntelligenceCanary() {
  return coreMutate("/email-intelligence/backfills", "POST", {
    mode: "CANARY",
    maximumMessages: 20,
  });
}

export function startEmailIntelligenceHistoricalBackfill(maximumMessages = 20) {
  return coreMutate("/email-intelligence/backfills", "POST", {
    mode: "HISTORICAL",
    maximumMessages,
  });
}

export function startEmailBusinessBackfill(maximumInvocations = 250) {
  return coreMutate("/email-intelligence/business-backfills", "POST", {
    maximumInvocations,
  });
}
